import { createSlice } from "@reduxjs/toolkit";

import {
    fetchUnclaimedOrders,
    markOrderAsUnclaimed,
    markUnclaimedOrderHandled,
} from "./thunk";

export interface UnclaimedOrderMeta {
    page: number;
    pageSize: number;
    total: number | null;
    hasNext: boolean;
    query: string;
}

export interface LoadingState {
    fetch: boolean;
    loadMore: boolean;
    markHandled: Record<string, boolean>;
}

export interface SuccessState {
    fetch: boolean;
    markHandled: Record<string, boolean>;
}

export interface UnclaimedOrdersState {
    orders: any[];
    meta: UnclaimedOrderMeta;
    loading: LoadingState;
    success: SuccessState;
    error: string | null;
}

const DEFAULT_PAGE_SIZE = 20;

const parseNumber = (value: any): number | undefined => {
    if (typeof value === "number" && !Number.isNaN(value)) {
        return value;
    }

    if (typeof value === "string" && value.trim().length > 0) {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    return undefined;
};

const parseBoolean = (value: any): boolean | undefined => {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "number") {
        if (value === 1) {
            return true;
        }

        if (value === 0) {
            return false;
        }
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "yes", "y", "on", "có", "đúng", "next", "hasnext", "more"].includes(normalized)) {
            return true;
        }

        if (["false", "0", "no", "n", "off", "không", "none", "hasprevious", "done"].includes(normalized)) {
            return false;
        }
    }

    return undefined;
};

const extractOrders = (payload: any): any[] => {
    if (!payload) {
        return [];
    }

    if (Array.isArray(payload)) {
        return payload;
    }

    const candidates = [
        payload.orders,
        payload.items,
        payload.list,
        payload.results,
        payload.data,
        payload.data?.orders,
        payload.data?.items,
        payload.data?.data,
        payload.payload,
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
            return candidate;
        }
    }

    return [];
};

const extractSingleOrder = (payload: any): any | undefined => {
    if (!payload) {
        return undefined;
    }

    if (Array.isArray(payload) && payload.length > 0) {
        return payload[0];
    }

    const candidates = [
        payload.order,
        payload.data,
        payload.result,
        payload.payload,
        payload.order?.data,
        payload.data?.order,
        payload.data?.data,
    ];

    for (const candidate of candidates) {
        if (!candidate) {
            continue;
        }

        if (Array.isArray(candidate) && candidate.length > 0) {
            return candidate[0];
        }

        if (typeof candidate === "object") {
            return candidate;
        }
    }

    if (typeof payload === "object") {
        return payload;
    }

    return undefined;
};

const normalizeId = (order: any): string | undefined => {
    if (!order || typeof order !== "object") {
        return undefined;
    }

    const candidates = [
        order.id,
        order.orderId,
        order.order_id,
        order.uuid,
        order.code,
        order.number,
        order.key,
    ];

    for (const candidate of candidates) {
        if (candidate === undefined || candidate === null) {
            continue;
        }

        if (typeof candidate === "string" && candidate.trim().length > 0) {
            return candidate;
        }

        if (typeof candidate === "number" && !Number.isNaN(candidate)) {
            return String(candidate);
        }
    }

    return undefined;
};

const deriveMeta = (
    payload: any,
    previousMeta: UnclaimedOrderMeta,
    params: Record<string, any> | undefined,
    receivedLength: number
): UnclaimedOrderMeta => {
    const meta: UnclaimedOrderMeta = { ...previousMeta };

    if (typeof params?.query === "string") {
        meta.query = params.query;
    }

    const paramPage = parseNumber(
        params?.page ?? params?.pageNumber ?? params?.page_index ?? params?.currentPage ?? params?.pageNo
    );
    if (paramPage !== undefined) {
        meta.page = paramPage;
    }

    const paramPageSize = parseNumber(
        params?.pageSize ?? params?.limit ?? params?.perPage ?? params?.per_page ?? params?.page_size
    );
    if (paramPageSize !== undefined) {
        meta.pageSize = paramPageSize;
    }

    let hasNextDefined = false;
    const metaSources = [payload?.meta, payload?.data?.meta, payload?.pagination, payload?.data?.pagination];

    for (const source of metaSources) {
        if (!source || typeof source !== "object") {
            continue;
        }

        const sourcePage = parseNumber(
            source.page ?? source.currentPage ?? source.pageNumber ?? source.page_index ?? source.pageNo
        );
        if (sourcePage !== undefined) {
            meta.page = sourcePage;
        }

        const sourcePageSize = parseNumber(
            source.pageSize ?? source.perPage ?? source.per_page ?? source.limit ?? source.page_size
        );
        if (sourcePageSize !== undefined) {
            meta.pageSize = sourcePageSize;
        }

        const sourceTotal = parseNumber(
            source.total ?? source.totalItems ?? source.totalCount ?? source.count ?? source.total_records
        );
        if (sourceTotal !== undefined) {
            meta.total = sourceTotal;
        }

        const sourceHasNext = parseBoolean(
            source.hasNext ?? source.hasMore ?? source.has_next ?? source.more ?? source.next
        );
        if (sourceHasNext !== undefined) {
            meta.hasNext = sourceHasNext;
            hasNextDefined = true;
        }
    }

    if (!hasNextDefined) {
        if (meta.total !== null && meta.pageSize > 0) {
            meta.hasNext = meta.page * meta.pageSize < meta.total;
        } else {
            meta.hasNext = receivedLength >= meta.pageSize;
        }
    }

    return meta;
};

export const initialState: UnclaimedOrdersState = {
    orders: [],
    meta: {
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        total: null,
        hasNext: false,
        query: "",
    },
    loading: {
        fetch: false,
        loadMore: false,
        markHandled: {},
    },
    success: {
        fetch: false,
        markHandled: {},
    },
    error: null,
};

const UnclaimedOrdersSlice = createSlice({
    name: "unclaimedOrders",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchUnclaimedOrders.pending, (state, action) => {
            const appendMode = Boolean(action.meta.arg?.append);
            state.loading.fetch = !appendMode;
            state.loading.loadMore = appendMode;
            state.success.fetch = false;
            state.error = null;
        });

        builder.addCase(fetchUnclaimedOrders.fulfilled, (state, action) => {
            const appendMode = Boolean(action.meta.arg?.append);
            const items = extractOrders(action.payload);
            const nextMeta = deriveMeta(action.payload, state.meta, action.meta.arg, items.length);

            if (appendMode) {
                const existingIndices = new Map<string, number>();
                state.orders.forEach((order, index) => {
                    const id = normalizeId(order);
                    if (id) {
                        existingIndices.set(id, index);
                    }
                });

                items.forEach((item) => {
                    const id = normalizeId(item);
                    if (id && existingIndices.has(id)) {
                        const targetIndex = existingIndices.get(id)!;
                        state.orders[targetIndex] = item;
                    } else {
                        if (id) {
                            existingIndices.set(id, state.orders.length);
                        }
                        state.orders.push(item);
                    }
                });
            } else {
                state.orders = items;
            }

            state.meta = nextMeta;
            state.loading.fetch = false;
            state.loading.loadMore = false;
            state.success.fetch = true;
            state.error = null;
        });

        builder.addCase(fetchUnclaimedOrders.rejected, (state, action) => {
            state.loading.fetch = false;
            state.loading.loadMore = false;
            state.success.fetch = false;
            state.error = typeof action.error?.message === "string" ? action.error.message : "Không thể tải dữ liệu";
        });

        builder.addCase(markUnclaimedOrderHandled.pending, (state, action) => {
            const orderId = normalizeId({ id: action.meta.arg?.orderId }) ?? String(action.meta.arg?.orderId ?? "");
            if (orderId) {
                state.loading.markHandled[orderId] = true;
                state.success.markHandled[orderId] = false;
            }
        });

        builder.addCase(markUnclaimedOrderHandled.fulfilled, (state, action) => {
            const payloadOrder = extractSingleOrder(action.payload);
            let targetId = normalizeId(payloadOrder);
            if (!targetId) {
                targetId = normalizeId({ id: action.meta.arg?.orderId }) ?? undefined;
            }

            if (targetId) {
                state.orders = state.orders.filter((order) => normalizeId(order) !== targetId);
                state.loading.markHandled[targetId] = false;
                state.success.markHandled[targetId] = true;
                if (state.meta.total !== null) {
                    state.meta.total = Math.max(0, state.meta.total - 1);
                }
            }

            state.error = null;
        });

        builder.addCase(markUnclaimedOrderHandled.rejected, (state, action) => {
            const targetId = normalizeId({ id: action.meta.arg?.orderId }) ?? String(action.meta.arg?.orderId ?? "");
            if (targetId) {
                state.loading.markHandled[targetId] = false;
                state.success.markHandled[targetId] = false;
            }
            state.error = typeof action.error?.message === "string" ? action.error.message : state.error;
        });

        builder.addCase(markOrderAsUnclaimed.fulfilled, (state, action) => {
            const payloadOrder = extractSingleOrder(action.payload);
            if (!payloadOrder || typeof payloadOrder !== "object") {
                return;
            }

            const orderId = normalizeId(payloadOrder);
            if (!orderId) {
                state.orders = [payloadOrder, ...state.orders];
            } else {
                const existingIndex = state.orders.findIndex((order) => normalizeId(order) === orderId);
                if (existingIndex >= 0) {
                    state.orders[existingIndex] = payloadOrder;
                } else {
                    state.orders = [payloadOrder, ...state.orders];
                }

                if (state.loading.markHandled[orderId] !== undefined) {
                    state.loading.markHandled[orderId] = false;
                }
            }

            if (state.meta.total !== null) {
                state.meta.total += 1;
            }
        });
    },
});

export default UnclaimedOrdersSlice.reducer;

